import type { ComponentInstance } from '../component.js';

export interface ControlHintsProps {
  embed: boolean;
  // 4.M: when true, renders touch hints (1 dito orbita / 2 dita pinch
  // / Tap hotspot) instead of keyboard shortcuts. The component
  // updates in place when pointerCoarse flips (Surface 2-in-1
  // keyboard plug/unplug).
  pointerCoarse: boolean;
}

interface HintRow {
  key: string;
  label: string;
}

const KEYBOARD_HINTS: readonly HintRow[] = [
  { key: 'F', label: 'Schermo intero' },
  { key: 'S', label: 'Cattura schermata' },
  { key: 'R', label: 'Reset vista' },
  { key: 'P', label: 'Statistiche FPS' },
];

const TOUCH_HINTS: readonly HintRow[] = [
  { key: '1 dito', label: 'orbita' },
  { key: '2 dita', label: 'pinch zoom' },
  { key: 'Tap', label: 'apri hotspot' },
];

export function mountControlHints(
  root: HTMLElement,
  initial: ControlHintsProps,
): ComponentInstance<ControlHintsProps> {
  const wrap = document.createElement('section');
  wrap.classList.add('viewer-ui-interactive');
  wrap.setAttribute('role', 'region');
  wrap.style.cssText = initial.embed
    ? 'position:fixed;bottom:0.5rem;left:0.5rem;background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);color:#f4f4f5;padding:0.375rem 0.625rem;border-radius:0.375rem;font-family:system-ui,sans-serif;font-size:0.75rem;z-index:11;pointer-events:auto;opacity:1;transition:opacity 600ms ease 4500ms'
    : 'position:fixed;bottom:1rem;left:1rem;background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);color:#f4f4f5;padding:0.625rem 0.875rem;border-radius:0.5rem;font-family:system-ui,sans-serif;font-size:0.875rem;z-index:11;pointer-events:auto;opacity:1;transition:opacity 600ms ease 4500ms';

  const heading = document.createElement('p');
  heading.style.cssText =
    'margin:0 0 0.375rem 0;font-size:0.6875rem;text-transform:uppercase;letter-spacing:0.05em;color:#a1a1aa';
  wrap.appendChild(heading);

  const list = document.createElement('ul');
  list.style.cssText =
    'margin:0;padding:0;list-style:none;display:flex;gap:0.875rem;flex-wrap:wrap';
  wrap.appendChild(list);

  function render(pointerCoarse: boolean): void {
    wrap.setAttribute('aria-label', pointerCoarse ? 'Suggerimenti tocco' : 'Suggerimenti tastiera');
    heading.textContent = pointerCoarse ? 'Tocco' : 'Scorciatoie';
    list.innerHTML = '';
    const hints = pointerCoarse ? TOUCH_HINTS : KEYBOARD_HINTS;
    for (const hint of hints) {
      const li = document.createElement('li');
      li.style.cssText = 'display:flex;align-items:center;gap:0.375rem';
      const kbd = document.createElement('kbd');
      kbd.style.cssText =
        'background:rgba(255,255,255,0.1);border:1px solid rgba(255,255,255,0.15);border-radius:0.25rem;padding:0 0.375rem;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.6875rem';
      kbd.textContent = hint.key;
      const span = document.createElement('span');
      span.textContent = hint.label;
      li.appendChild(kbd);
      li.appendChild(span);
      list.appendChild(li);
    }
  }
  render(initial.pointerCoarse);

  root.appendChild(wrap);

  // Fade out automatically after CSS transition delay.
  const timer = setTimeout(() => {
    wrap.style.opacity = '0';
  }, 50);
  const removeTimer = setTimeout(() => {
    wrap.remove();
  }, 6500);

  return {
    update(props: ControlHintsProps) {
      // Re-render in place when pointer modality changes live.
      render(props.pointerCoarse);
    },
    cleanup() {
      clearTimeout(timer);
      clearTimeout(removeTimer);
      wrap.remove();
    },
  };
}
